import React, { useContext, useState } from "react";
import { FaUpload, FaTimes } from "react-icons/fa";
import Utils from "../../middleware/Utils";

import Select from "react-select";
import { globalSelectStyles } from "../../middleware/enum";
import { AuthContext, LoaderContext } from "../../Context/ContextFunctions";
import {
  UploadCustomerDocument,
  UploadFile,
} from "../../services/DocumetUpload/DocumentUploadApi";
import SuccessModal from "./SuccessModal";

const FileUploadModal = ({
  show,
  onClose,
  ModuleName,
  setIsAddUpdateDone,
  customerKeyID,
}) => {
  // Context
  const { setLoader } = useContext(LoaderContext);
  const [errorMessage, setErrorMessage] = useState(false);
  const { authDetails } = useContext(AuthContext);
  const [showSuccessPopupModal, setShowSuccessPopupModal] = useState(false);
  const [DocumentObj, setDocumentObj] = useState({
    selectedFile: null,
    selectedClient: null,
    documentTypeID: null,
    documentName: "",
  });

  const handleChange = (field, value) => {
    setDocumentObj((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrorMessage(false); // Clear error messages on any changes
  };

  const handleFileChange = (event) => {
    if (event.target.files.length > 0) {
      handleChange("selectedFile", event.target.files[0]);
    }
  };

  const handleDragOver = (event) => event.preventDefault();

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      handleChange("selectedFile", event.dataTransfer.files[0]);
    }
  };

  const uploadDocument = async () => {
    setLoader(true);
    try {
      const isValid =
        DocumentObj.selectedFile &&
        DocumentObj.documentTypeID &&
        DocumentObj.documentName &&
        (DocumentObj.selectedClient || ModuleName !== undefined);

      if (!isValid) {
        setLoader(false);
        setErrorMessage("Please fill all required fields.");
        return;
      }

      const fileFormData = new FormData();
      fileFormData.append("file", DocumentObj.selectedFile);

      console.log("Starting upload with data:", {
        docName: DocumentObj.documentName,
        adminKeyID: authDetails.adminKeyID,
        documentTypeID: DocumentObj.documentTypeID,
      });

      let response;
      if (customerKeyID) {
        // Call UploadFile API
        response = await UploadFile(
          "Customer",
          DocumentObj.documentName,
          DocumentObj.documentTypeID,
          authDetails.adminKeyID,
          customerKeyID, // Pass customerKeyID to UploadFile

          fileFormData
        );
      } else {
        // Call UploadClientDocument API
        response = await UploadCustomerDocument(
          DocumentObj.documentName,
          DocumentObj.documentTypeID,
          authDetails.adminKeyID,
          fileFormData
        );
      }

      console.log("Upload response:", response);

      if (response === true) {
        // Adjust based on your API response structure
        setIsAddUpdateDone(true);
        setShowSuccessPopupModal(true);
        setErrorMessage(false);
      } else {
        setErrorMessage(response?.message || "Something went wrong.");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setErrorMessage("An unexpected error occurred.");
    } finally {
      setLoader(false);
    }
  };

  const handleCancel = () => {
    setDocumentObj({
      selectedFile: null,
      selectedClient: null,
      documentTypeID: null,
      documentName: "",
    });
    setShowSuccessPopupModal(false);
    onClose();
    setErrorMessage(false);
  };

  if (!show) return null;

  const isFormValid =
    DocumentObj.selectedFile &&
    DocumentObj.documentTypeID &&
    DocumentObj.documentName &&
    (ModuleName !== undefined || DocumentObj.selectedClient);

  return (
    <div className="z-10 fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-[600px] relative flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 flex justify-between items-center border-b pb-2 bg-[#ECF6FF]">
          <h3 className="text-lg font-semibold">Upload File</h3>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900"
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-grow px-4 py-4 max-h-[60vh] text-sm">
          <div className="grid grid-cols-2 gap-4">
            {ModuleName === undefined && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Select Client <span className="text-red-500">*</span>
                </label>
                <Select
                  className="w-full"
                  options={[
                    { value: 1, label: "Client 1" },
                    { value: 2, label: "Client 2" },
                  ]}
                  value={DocumentObj.selectedClient}
                  onChange={(value) => handleChange("selectedClient", value)}
                  placeholder="Select Client"
                  styles={globalSelectStyles}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Receiver Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                value={authDetails.firstName + " " + authDetails.lastName}
                disabled
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Document Type <span className="text-red-500">*</span>
              </label>
              <Select
                className="w-full"
                options={Utils.DocumentType || []}
                value={Utils.DocumentType.find(
                  (item) => item.value === DocumentObj.documentTypeID
                )}
                onChange={(value) =>
                  handleChange("documentTypeID", value.value)
                }
                placeholder="Select Document Type"
                styles={globalSelectStyles}
              />
            </div>

            <div>
              <label
                htmlFor="documentName"
                className="block text-sm font-medium text-gray-700"
              >
                Document Name <span className="text-red-500">*</span>
              </label>
              <input
                id="documentName"
                type="text"
                className="w-full p-2 border rounded-md"
                value={DocumentObj.documentName}
                onChange={(e) => handleChange("documentName", e.target.value)}
                placeholder="Enter Document Name"
              />
            </div>
          </div>

          {/* File Upload */}
          {!DocumentObj.selectedFile && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Upload File <span className="text-red-500">*</span>
              </label>
              <div
                className="border-2 border-dashed border-gray-400 rounded-lg p-6 flex flex-col items-center justify-center text-gray-500 cursor-pointer hover:bg-gray-100 transition-all"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => document.getElementById("fileInput").click()}
              >
                <FaUpload className="text-4xl mb-2" />
                <p>Drag & Drop your file here</p>
                <p className="text-sm text-gray-700">or click to browse</p>
              </div>
            </div>
          )}

          <input
            id="fileInput"
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />

          {DocumentObj.selectedFile && (
            <div className="mt-4 p-2 bg-gray-100 rounded-md flex justify-between items-center">
              <span className="text-sm text-gray-700">
                {DocumentObj.selectedFile.name}
              </span>
              <button
                onClick={() => handleChange("selectedFile", null)}
                className="text-red-500"
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
        {errorMessage && (
          <div className="text-center">
            <label className="validation ">{errorMessage}</label>
          </div>
        )}
        {/* Footer */}
        <div className="flex justify-end border-t pt-4 space-x-2 p-4 bg-gray-100 text-sm">
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={uploadDocument}
            disabled={!isFormValid}
            className={`px-4 py-2 rounded-md ${
              isFormValid
                ? "bg-[#E86B1B] text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Upload
          </button>
        </div>
      </div>
      <SuccessModal
        isOpen={showSuccessPopupModal}
        onClose={handleCancel}
        modelAction={"Add"}
        actionMessage={DocumentObj.documentName}
      />
    </div>
  );
};

export default FileUploadModal;
